import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom"; // Add this import
import {
  GraduationCap,
  Plane,
  Globe2,
  ArrowRight,
  BookOpen,
  Briefcase,
  Award,
} from "lucide-react";
import FlightTicket from "../assets/flight-ticket.jpg";
import MigrationService from "../assets/migration.jpg";
import StudyAbroad from "../assets/study-abroad.jpg";
import { Button } from "../components/ui/Button";
import { openWhatsApp } from "../utils/whatsapp";
import { SEO } from "../components/SEO";

// Service data with route paths
const services = [
  {
    id: 1,
    title: "Study Abroad Services",
    slug: "study-abroad", // Add slug for URL
    description:
      "We provide comprehensive support for students aspiring to pursue their education abroad, guiding you through every step of your academic journey.",
    image: StudyAbroad,
    icon: <GraduationCap className="w-8 h-8" />,
    color: "#800020",
    features: [
      "Program selection and guidance",
      "University applications",
      "Scholarship support",
      "Visa processing",
      "Pre-departure briefing",
      "Accommodation assistance",
    ],
  },
  {
    id: 2,
    title: "Migration Services",
    slug: "migration", // Add slug for URL
    description:
      "Navigate the complexities of immigration with our expert guidance, helping you achieve your migration goals with confidence and ease.",
    image: MigrationService,
    icon: <Globe2 className="w-8 h-8" />,
    color: "#800020",
    features: [
      "Immigration consultation",
      "Visa eligibility assessment",
      "Permanent residency pathways",
      "Work visa processing",
      "Documentation support",
      "Family sponsorship guidance",
    ],
  },
  {
    id: 3,
    title: "Flight Ticket Booking",
    slug: "flight-booking", // Add slug for URL
    description:
      "Experience hassle-free travel booking with competitive prices and personalized service tailored to your travel needs.",
    image: FlightTicket,
    icon: <Plane className="w-8 h-8" />,
    color: "#800020",
    features: [
      "Local & international flights",
      "Best-price ticket search",
      "Group travel support",
      "Student travel discounts",
      "Flexible booking options",
      "24/7 travel support",
    ],
  },
];

const additionalServices = [
  {
    icon: <BookOpen className="w-10 h-10" />,
    title: "Career Counseling",
    description:
      "Expert guidance to align your education with career goals and industry trends.",
    color: "#800020",
  },
  {
    icon: <Briefcase className="w-10 h-10" />,
    title: "Visa Application Support",
    description:
      "Expert assistance with visa applications for various countries and purposes.",
    color: "#800020",
  },
  {
    icon: <Award className="w-10 h-10" />,
    title: "Study Guidance",
    description:
      "Find and apply for scholarships and study abroad programs opportunities worldwide.",
    color: "#800020",
  },
];

export default function ProgramsPage() {
  const navigate = useNavigate(); // kept in case navigation still used elsewhere

  // Handle CTA by opening WhatsApp with service context
  const handleLearnMore = (slug: string) => {
    const service = services.find((s) => s.slug === slug);
    openWhatsApp(
      `Hello! I am interested in ${service?.title ?? "your services"}. Please help me get started.\n\nName:\nEmail:\nPhone:`,
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Programs & Services"
        description="Explore our comprehensive study abroad, migration, and flight booking services. We provide expert guidance for students and professionals seeking international opportunities."
        ogTitle="Programs & Services - Study Abroad, Migration & Flight Booking"
        ogDescription="Discover our comprehensive services including study abroad consultation, visa assistance, migration support, and flight booking. Your partner for global opportunities."
      />
      {/* Hero Section */}
      <div className="relative bg-cover bg-center bg-no-repeat py-20 bg-gradient-to-br from-[#004E89] to-[#800020] md:py-28 lg:py-32">
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-poppins text-5xl md:text-7xl font-bold tracking-tight leading-tight antialiased mb-6 text-white">
            Our Programs & Services
          </h1>
          <p className="font-jakarta text-xl md:text-2xl tracking-tight leading-relaxed antialiased text-white">
            Comprehensive solutions for your global education and migration
            journey
          </p>
        </div>
      </div>

      {/* Main Services Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div
                  className={`grid lg:grid-cols-2 gap-0 ${
                    index % 2 === 1 ? "lg:grid-flow-dense" : ""
                  }`}
                >
                  {/* Content Side */}
                  <div
                    className={`p-8 lg:p-12 flex flex-col justify-center ${
                      index % 2 === 1 ? "lg:col-start-2" : ""
                    }`}
                  >
                    {/* Icon */}
                    <div
                      className="inline-flex w-16 h-16 items-center justify-center rounded-2xl mb-6"
                      style={{
                        backgroundColor: `${service.color}15`,
                        color: service.color,
                      }}
                    >
                      {service.icon}
                    </div>

                    {/* Title */}
                    <h3 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight leading-tight antialiased">
                      {service.title}
                    </h3>

                    {/* Description */}
                    <p className="font-jakarta text-gray-600 text-lg mb-6 tracking-tight leading-relaxed antialiased">
                      {service.description}
                    </p>

                    {/* Features List */}
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                      {service.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start font-jakarta text-gray-700 tracking-tight antialiased"
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0"
                            style={{ backgroundColor: service.color }}
                          />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button - Updated with onClick */}
                    <Button
                      className="self-start"
                      style={{ backgroundColor: service.color }}
                      onClick={() => handleLearnMore(service.slug)}
                      rightIcon={<ArrowRight className="w-5 h-5" />}
                    >
                      Learn More
                    </Button>
                  </div>

                  {/* Image Side */}
                  <div
                    className={`relative h-64 lg:h-auto ${
                      index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""
                    }`}
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div
                      className="absolute inset-0 opacity-20"
                      style={{
                        background: `linear-gradient(135deg, ${service.color} 0%, transparent 100%)`,
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-poppins text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
              Additional Services
            </h2>
            <p className="font-jakarta text-lg text-gray-600 max-w-2xl mx-auto tracking-tight leading-relaxed antialiased">
              More ways we support your journey to success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-gray-50 rounded-2xl p-8 hover:bg-gray-100 transition-colors"
              >
                <div
                  className="inline-flex w-16 h-16 items-center justify-center rounded-2xl mb-6"
                  style={{
                    backgroundColor: `${service.color}15`,
                    color: service.color,
                  }}
                >
                  {service.icon}
                </div>
                <h3 className="font-poppins text-2xl font-bold text-gray-900 mb-3 tracking-tight antialiased">
                  {service.title}
                </h3>
                <p className="font-jakarta text-gray-600 tracking-tight leading-relaxed antialiased">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-poppins text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
              How We Work
            </h2>
            <p className="font-jakarta text-lg text-gray-600 max-w-2xl mx-auto tracking-tight leading-relaxed antialiased">
              Our simple 4-step process to get you started
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Consultation",
                description: "Book a free consultation to discuss your goals",
              },
              {
                step: "02",
                title: "Planning",
                description: "We create a personalized plan for your journey",
              },
              {
                step: "03",
                title: "Application",
                description: "We handle all applications and documentation",
              },
              {
                step: "04",
                title: "Success",
                description: "You're on your way to your dream destination!",
              },
            ].map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <div className="inline-flex w-20 h-20 items-center justify-center rounded-full bg-gradient-to-br from-[#004E89] to-[#800020] text-white font-poppins text-2xl font-bold mb-4">
                  {step.step}
                </div>
                <h3 className="font-poppins text-xl font-bold text-gray-900 mb-2 tracking-tight antialiased">
                  {step.title}
                </h3>
                <p className="font-jakarta text-gray-600 tracking-tight antialiased">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#004E89] to-[#800020]">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight antialiased">
              Ready to Start Your Journey?
            </h2>
            <p className="font-jakarta text-xl text-white/90 mb-8 tracking-tight antialiased">
              Book a consultation with our expert advisors and take the first
              step toward your global future.
            </p>
            <Button
              variant="primary"
              size="lg"
              rightIcon={<ArrowRight className="w-5 h-5" />}
              onClick={() =>
                openWhatsApp(
                  "Hello! I would like to book a consultation with BeginPack Ltd.\n\nName:\nEmail:\nPhone:\nService of interest:",
                )
              }
            >
              Contact Us
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
